import { SCREEN_NAME } from 'constants/index';
import {
  CommunityAnalyticsClient,
  eventType,
} from '@atlassian/community-library';
import { ENVIRONMENT, getEnvironment } from 'utils';

let eagerInitDone = false;

/**
 * Eagerly initialises the analytics client WITHOUT waiting for OneTrust.
 * Used exclusively for operational events which are strictly-necessary and
 * exempt from C0002 (Performance) consent. The library's Tracking.trackEvent()
 * allows operational events through regardless of consent state, and
 * GASv3.sendOperationalEvent() strips cross-product identifiers when opted out.
 *
 * This eager initialisation intentionally does not provide a userId because
 * operational events may need to be emitted before user context is available.
 * In that case, early operational events can be sent without user context.
 * Callers should call setUserInfo() before sendOperationalEvent() where
 * possible so subsequent analytics events include user context.
 * */
const ensureEagerInit = (): void => {
  if (eagerInitDone || CommunityAnalyticsClient.hasAnalyticsClient()) {
    return;
  }
  CommunityAnalyticsClient.initializeClient(
    CommunityAnalyticsClient.SUB_APP.UNIFIED_PORTABLE_PROFILE,
    null,
    getEnvironment() as ENVIRONMENT,
    null,
  );
  eagerInitDone = true;
};

export const setUserInfo = (aaid: string) => {
  CommunityAnalyticsClient.setAnalyticsUserInfo(aaid);
};

type ScreenEventProps = {
  screenName: SCREEN_NAME;
  attributes?: { [key: string]: string | boolean | number };
  isPersonalView: boolean;
};

export const sendScreenEvent = ({
  screenName,
  attributes,
  isPersonalView,
}: ScreenEventProps) => {
  CommunityAnalyticsClient.sendEvent(eventType.SCREEN, {
    name: screenName,
    attributes: {
      isPersonalView,
      ...attributes,
    },
  });
};

type TrackEventProps = {
  source: SCREEN_NAME;
  action: string;
  actionSubject: string;
  actionSubjectId?: string;
  attributes?: { [key: string]: string | boolean | number };
  isPersonalView: boolean;
};

export const sendTrackEvent = ({
  source,
  action,
  actionSubject,
  actionSubjectId,
  attributes,
  isPersonalView,
}: TrackEventProps) => {
  CommunityAnalyticsClient.sendEvent(eventType.TRACK, {
    action,
    actionSubject,
    attributes: {
      isPersonalView,
      ...attributes,
    },
    source,
    actionSubjectId,
  });
};

type UIEventProps = {
  source: SCREEN_NAME;
  action: string;
  actionSubject: string;
  actionSubjectId?: string;
  attributes?: { [key: string]: string | boolean | number };
  isPersonalView: boolean;
};

export const sendUIEvent = ({
  source,
  action,
  actionSubject,
  actionSubjectId,
  attributes,
  isPersonalView,
}: UIEventProps) => {
  CommunityAnalyticsClient.sendEvent(eventType.UI, {
    action,
    actionSubject,
    attributes: {
      isPersonalView,
      ...attributes,
    },
    source,
    actionSubjectId,
  });
};

type OperationalEventProps = {
  source: string;
  action: string;
  actionSubject: string;
  attributes?: { [key: string]: string | boolean | number };
};

/**
 * Sends an operational event immediately without waiting for OneTrust consent.
 * Operational events are strictly-necessary for page-lifecycle observability and
 * are exempt from C0002 (Performance) consent gating.
 */
export const sendOperationalEvent = ({
  source,
  action,
  actionSubject,
  attributes,
}: OperationalEventProps) => {
  ensureEagerInit();
  CommunityAnalyticsClient.sendEvent(eventType.OPERATIONAL, {
    source,
    action,
    actionSubject,
    attributes,
  });
};
